import { useState, useRef, useEffect } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'

import { useAuth } from '../../../context/AuthContext'
import { useUserRelations } from '../../../context/UserRelationsContext'
import FPWLogo from '../../../images/FPW_Logo.png'
import { isScopeRestricted } from '../../../utils/permissions'
import NotificationBell from '../../NotificationBell'
import { SearchBar } from '../../SearchBar'

import styles from './Navigation.module.css'

type NavigationProps = {
  isLoggedIn: boolean
  handleSignIn: () => Promise<void>
  handleSignOut: () => Promise<void>
  screen_name?: string
}

export const Navigation = ({ isLoggedIn, handleSignIn, handleSignOut, screen_name }: NavigationProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const profileMenuRef = useRef<HTMLDivElement>(null)
  const { isModerator } = useUserRelations()
  const { restrictedActions } = useAuth()
  const patternBlocked = isScopeRestricted(restrictedActions, 'pattern')
  const projectBlocked = isScopeRestricted(restrictedActions, 'project')

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node)) {
        setProfileMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  if (location.pathname.includes('/callback')) return null

  if (!isLoggedIn) {
    return (
      <header className={`${styles.stickyHeader} ${styles.notLoggedIn}`}>
        <div className={styles.navContainer}>
          <Link to="/" className={styles.logoSection} aria-label="Home">
            <img src={FPWLogo} alt="Free Pattern Warehouse Logo" className={styles.logoImg} />
            <span className={styles.siteTitle}>Free Pattern Warehouse</span>
          </Link>

          <div className={styles.searchSection}>
            <SearchBar placeholder="Search patterns and users..." />
          </div>

          <div className={styles.rightControls}>
            <button className={styles.signInBtn} onClick={handleSignIn} aria-label="Log in to your account">
              Log In
            </button>
          </div>
        </div>
      </header>
    )
  }

  return (
    <header className={styles.stickyHeader}>
      <div className={styles.navContainer}>
        <Link to="/" className={styles.logoSection} aria-label="Home">
          <img src={FPWLogo} alt="Free Pattern Warehouse Logo" className={styles.logoImg} />
          <span className={styles.siteTitle}>Free Pattern Warehouse</span>
        </Link>

        <div className={styles.searchSection}>
          <SearchBar placeholder="Search patterns and users..." />
        </div>

        <div className={styles.rightControls}>
          {isModerator && (
            <button
              className={styles.createBtn}
              onClick={() => navigate('/moderation')}
              aria-label="Moderation dashboard"
            >
              Moderation
            </button>
          )}

          {screen_name && (
            <div className={styles.notificationBell}>
              <NotificationBell />
            </div>
          )}

          <div className={styles.profileWrapper} ref={profileMenuRef}>
            <button
              className={styles.profileBtn}
              onClick={() => setProfileMenuOpen((o) => !o)}
              aria-expanded={profileMenuOpen}
              aria-haspopup="true"
              aria-label="User menu"
            >
              <span className={styles.profileName}>{screen_name || 'Account'}</span>
              <svg
                className={styles.caretIcon}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {profileMenuOpen && screen_name && (
              <div className={styles.profileDropdown} role="menu">
                <button
                  className={styles.dropdownItem}
                  onClick={() => {
                    navigate(`/author/${screen_name}`)
                    setProfileMenuOpen(false)
                  }}
                  role="menuitem"
                >
                  My Profile
                </button>
                <button
                  className={styles.dropdownItem}
                  onClick={() => {
                    navigate(`/craft-rooms/${screen_name}`)
                    setProfileMenuOpen(false)
                  }}
                  role="menuitem"
                >
                  My Craft Room
                </button>
                <button
                  className={styles.dropdownItem}
                  onClick={() => {
                    if (patternBlocked) return
                    navigate('/create')
                    setProfileMenuOpen(false)
                  }}
                  disabled={patternBlocked}
                  style={{ opacity: patternBlocked ? 0.5 : 1, cursor: patternBlocked ? 'not-allowed' : 'pointer' }}
                  title={patternBlocked ? 'Your account is restricted from this action.' : undefined}
                  role="menuitem"
                >
                  Create Pattern
                </button>
                <button
                  className={styles.dropdownItem}
                  onClick={() => {
                    if (projectBlocked) return
                    navigate('/craft-projects/create')
                    setProfileMenuOpen(false)
                  }}
                  disabled={projectBlocked}
                  style={{ opacity: projectBlocked ? 0.5 : 1, cursor: projectBlocked ? 'not-allowed' : 'pointer' }}
                  title={projectBlocked ? 'Your account is restricted from this action.' : undefined}
                  role="menuitem"
                >
                  Create Project
                </button>
                <button
                  className={styles.dropdownItem}
                  onClick={() => {
                    navigate('/materials/discover')
                    setProfileMenuOpen(false)
                  }}
                  role="menuitem"
                >
                  Discover Materials
                </button>
                <button
                  className={styles.dropdownItem}
                  onClick={() => {
                    navigate('/notifications')
                    setProfileMenuOpen(false)
                  }}
                  role="menuitem"
                >
                  Notifications
                </button>
                <button
                  className={styles.dropdownItem}
                  onClick={() => {
                    navigate('/settings')
                    setProfileMenuOpen(false)
                  }}
                  role="menuitem"
                >
                  Settings
                </button>
                <hr className={styles.dropdownDivider} />
                <button
                  className={`${styles.dropdownItem} ${styles.signOutBtn}`}
                  onClick={() => {
                    handleSignOut()
                    setProfileMenuOpen(false)
                  }}
                  role="menuitem"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}

export default Navigation
